import React, { useEffect, useMemo, useState } from "react";
import confetti from "canvas-confetti";
import {
  DollarSign,
  X,
  CheckCircle,
  Sparkles,
  Flame,
  Film,
  AlertCircle,
} from "lucide-react";
import api from "../lib/api";

const brl = (n) =>
  `R$ ${Number(n || 0)
    .toFixed(2)
    .replace(".", ",")
    .replace(/\B(?=(\d{3})+(?!\d))/g, ".")}`;

const ACTIVE_STATES = ["ready", "in_test", "validated", "prescale", "scaling"];

const STATUS_LABELS = {
  ready: "Pronto",
  in_test: "Em Teste",
  validated: "Validado",
  scaling: "Escalando",
  prescale: "Pré-Escala",
  discarded: "Descartado",
  idea: "Ideia",
  in_production: "Em Produção",
};

export default function RegisterSaleModal({
  isOpen,
  onClose,
  onSaleSuccess,
  activeCampaignId,
  preselectedCreativeId,
}) {
  const [value, setValue] = useState("197.00");
  const [quantity, setQuantity] = useState(1);
  const [creativeId, setCreativeId] = useState(preselectedCreativeId || "");
  const [creatives, setCreatives] = useState([]);
  const [creativesLoading, setCreativesLoading] = useState(false);
  const [creativesError, setCreativesError] = useState("");
  const [search, setSearch] = useState("");
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [successData, setSuccessData] = useState(null);

  useEffect(() => {
    if (!isOpen) return undefined;
    let alive = true;
    setCreativesLoading(true);
    (async () => {
      try {
        const res = await api.get("/api/creatives/");
        if (!alive) return;
        const all = res.data.creatives || [];
        // Only show creatives that are actually active in the operation.
        const active = all
          .filter((c) => ACTIVE_STATES.includes(c.status))
          .sort((a, b) => {
            // Preselected first, then most recent activity, then most recent update.
            if (a.id === preselectedCreativeId) return -1;
            if (b.id === preselectedCreativeId) return 1;
            const at = a.attributed_last_sale_at || a.updated_at || "";
            const bt = b.attributed_last_sale_at || b.updated_at || "";
            return bt.localeCompare(at);
          });
        setCreatives(active);
        if (!creativeId) {
          const preselected = active.find((c) => c.id === preselectedCreativeId) || active[0];
          if (preselected) setCreativeId(preselected.id);
        }
      } catch (err) {
        setCreativesError(
          "Não foi possível carregar seus criativos. Verifique a conexão e tente novamente."
        );
        // eslint-disable-next-line no-console
        console.warn("[sale] creatives fetch failed:", err?.message || err);
      } finally {
        if (alive) setCreativesLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
    // creativeId intentionally excluded — it's an internal side effect target.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, preselectedCreativeId]);

  const filteredCreatives = useMemo(() => {
    if (!search.trim()) return creatives;
    const q = search.trim().toLowerCase();
    return creatives.filter(
      (c) =>
        (c.name || "").toLowerCase().includes(q) ||
        (c.product || "").toLowerCase().includes(q)
    );
  }, [creatives, search]);

  const selectedCreative = useMemo(
    () => creatives.find((c) => c.id === creativeId),
    [creatives, creativeId]
  );

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError("");
    if (!creativeId) {
      setSubmitError("Selecione o criativo responsável pela venda.");
      return;
    }
    setLoading(true);
    try {
      const numVal = parseFloat(String(value).replace(",", "."));
      // Only forward the caller-supplied campaign_id when the user kept the
      // preselected creative. If they picked a different creative, let the
      // backend resolve the correct campaign deterministically to avoid a
      // "campanha não pertence a este criativo" 400.
      const keepCampaign =
        preselectedCreativeId && creativeId === preselectedCreativeId;
      const res = await api.post("/api/sales/", {
        value: numVal,
        quantity: parseInt(quantity, 10),
        creative_id: creativeId,
        campaign_id: keepCampaign ? activeCampaignId || undefined : undefined,
        notes: notes || undefined,
      });

      confetti({
        particleCount: 80,
        spread: 70,
        origin: { y: 0.6 },
        colors: ["#FF6B00", "#10B981", "#FFD700", "#ffffff"],
      });

      setSuccessData(res.data);
      if (onSaleSuccess) onSaleSuccess();
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        "Erro ao registrar venda. Verifique o valor e o criativo informados.";
      setSubmitError(detail);
      // eslint-disable-next-line no-console
      console.error("Sale error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSuccessData(null);
    setSubmitError("");
    setSearch("");
    setNotes("");
    onClose();
  };

  return (
    <div
      data-testid="register-sale-modal-overlay"
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
    >
      <div
        data-testid="register-sale-modal"
        className="bg-[#14161C] border-t sm:border border-[#282D3D] rounded-t-2xl sm:rounded-2xl w-full max-w-md p-5 sm:p-6 relative shadow-[0_10px_40px_rgba(0,0,0,0.8)] max-h-[90vh] overflow-y-auto"
      >
        <button
          data-testid="close-sale-modal-btn"
          onClick={handleClose}
          className="absolute top-3 right-3 text-gray-400 hover:text-white p-1 rounded-md"
        >
          <X className="w-5 h-5" />
        </button>

        {!successData ? (
          <div>
            <div className="flex items-center gap-2 mb-4">
              <div className="w-9 h-9 rounded-lg bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center text-emerald-400">
                <DollarSign className="w-5 h-5" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-white leading-none">Registrar Venda</h2>
                <span className="text-xs text-gray-400">
                  Atendimento convertido no WhatsApp
                </span>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-3">
              {/* 1. Valor */}
              <div>
                <label className="block text-xs font-semibold text-gray-300 mb-1">
                  Valor da Venda (R$) *
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-2.5 text-gray-400 font-bold text-sm">
                    R$
                  </span>
                  <input
                    type="number"
                    step="0.01"
                    min="1"
                    inputMode="decimal"
                    data-testid="sale-value-input"
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    required
                    className="w-full bg-[#1A1D26] border border-[#282D3D] rounded-lg py-2 pl-10 pr-3 text-white text-base font-bold focus:outline-none focus:border-[#FF6B00]"
                    placeholder="197,00"
                  />
                </div>
                <div className="flex gap-1.5 mt-1.5">
                  {["97", "197", "297"].map((v) => (
                    <button
                      type="button"
                      key={v}
                      data-testid={`sale-shortcut-${v}`}
                      onClick={() => setValue(v)}
                      className="flex-1 py-1.5 bg-[#1F2330] hover:bg-[#2A3040] text-gray-300 text-xs rounded border border-[#282D3D] font-semibold"
                    >
                      R${v}
                    </button>
                  ))}
                </div>
              </div>

              {/* 2. Creative (mandatory) */}
              <div>
                <label className="block text-xs font-semibold text-gray-300 mb-1 flex items-center gap-1">
                  <Film className="w-3.5 h-3.5 text-[#FF6B00]" />
                  <span>Criativo Responsável *</span>
                </label>

                {creatives.length > 5 && (
                  <input
                    type="text"
                    data-testid="sale-creative-search"
                    placeholder="Buscar criativo..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="w-full mb-2 bg-[#1A1D26] border border-[#282D3D] rounded-lg py-1.5 px-3 text-white text-xs focus:outline-none focus:border-[#FF6B00]"
                  />
                )}

                {creativesLoading ? (
                  <div className="text-xs text-gray-400 py-3 text-center">
                    Carregando criativos...
                  </div>
                ) : creativesError ? (
                  <div className="p-2 rounded-lg bg-red-500/10 border border-red-500/30 text-[11px] text-red-300">
                    {creativesError}
                  </div>
                ) : filteredCreatives.length === 0 ? (
                  <div
                    data-testid="sale-no-creatives-warning"
                    className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/40 text-[11px] text-amber-300 flex items-start gap-2"
                  >
                    <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                    <span>
                      Você ainda não tem criativos ativos. Cadastre um criativo em{" "}
                      <strong>Criativos</strong> antes de registrar a venda — cada venda
                      precisa ser atribuída a um criativo da sua operação.
                    </span>
                  </div>
                ) : (
                  <div
                    data-testid="sale-creative-picker"
                    className="max-h-44 overflow-y-auto rounded-lg border border-[#282D3D] divide-y divide-[#232733]"
                  >
                    {filteredCreatives.slice(0, 20).map((c) => {
                      const active = c.id === creativeId;
                      return (
                        <button
                          type="button"
                          key={c.id}
                          data-testid={`sale-creative-option-${c.id}`}
                          onClick={() => setCreativeId(c.id)}
                          className={`w-full text-left p-2.5 flex items-center gap-2 transition-colors ${
                            active
                              ? "bg-[#FF6B00]/15 border-l-2 border-l-[#FF6B00]"
                              : "hover:bg-[#1B1E28]"
                          }`}
                        >
                          <div
                            className={`w-2 h-2 rounded-full shrink-0 ${
                              active ? "bg-[#FF6B00]" : "bg-gray-600"
                            }`}
                          />
                          <div className="flex-1 min-w-0">
                            <div className="text-xs font-bold text-white truncate">
                              {c.name}
                            </div>
                            <div className="text-[10px] text-gray-400 truncate">
                              {c.product} · {STATUS_LABELS[c.status] || c.status}
                              {c.attributed_sales_count > 0
                                ? ` · ${c.attributed_sales_count} venda(s)`
                                : ""}
                            </div>
                          </div>
                          {active && <CheckCircle className="w-4 h-4 text-[#FF6B00]" />}
                        </button>
                      );
                    })}
                  </div>
                )}
                {selectedCreative && (
                  <p className="text-[10px] text-gray-500 mt-1">
                    Atribuição: <span className="text-white">{selectedCreative.name}</span>
                  </p>
                )}
              </div>

              {/* 3. Quantidade */}
              <div>
                <label className="block text-xs font-semibold text-gray-300 mb-1">
                  Quantidade
                </label>
                <input
                  type="number"
                  min="1"
                  inputMode="numeric"
                  data-testid="sale-quantity-input"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  className="w-full bg-[#1A1D26] border border-[#282D3D] rounded-lg py-2 px-3 text-white text-sm focus:outline-none focus:border-[#FF6B00]"
                />
              </div>

              {/* 4. Observações */}
              <div>
                <label className="block text-xs font-semibold text-gray-300 mb-1">
                  Observações (Opcional)
                </label>
                <input
                  type="text"
                  data-testid="sale-notes-input"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Ex: Comprou kit 3 potes após áudio de suporte"
                  className="w-full bg-[#1A1D26] border border-[#282D3D] rounded-lg py-2 px-3 text-white text-xs focus:outline-none focus:border-[#FF6B00]"
                />
              </div>

              {submitError && (
                <div
                  data-testid="sale-submit-error"
                  className="p-2 rounded-lg bg-red-500/10 border border-red-500/40 text-[11px] text-red-300"
                >
                  {submitError}
                </div>
              )}

              <button
                type="submit"
                disabled={loading || !creativeId || creatives.length === 0}
                data-testid="confirm-register-sale-btn"
                className="w-full py-3 rounded-lg bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-400 hover:to-green-500 text-black font-extrabold text-sm uppercase tracking-wider transition-all shadow-[0_0_20px_rgba(16,185,129,0.3)] flex items-center justify-center gap-2 mt-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <span>Salvando...</span>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4" />
                    <span>Confirmar Venda (+150 XP)</span>
                  </>
                )}
              </button>
            </form>
          </div>
        ) : (
          <div data-testid="sale-success-feedback" className="text-center py-4">
            <div className="w-16 h-16 rounded-full bg-emerald-500/20 border-2 border-emerald-500 flex items-center justify-center text-emerald-400 mx-auto mb-3 animate-bounce">
              <CheckCircle className="w-8 h-8" />
            </div>

            <h2 className="text-xl font-extrabold text-white mb-1">💰 VENDA REGISTRADA!</h2>
            <p className="text-sm text-emerald-400 font-semibold mb-1">
              {brl(value)} somados à sua operação!
            </p>
            {successData.creative_name && (
              <p
                data-testid="sale-attribution-line"
                className="text-[11px] text-gray-400 mb-3"
              >
                Atribuída ao criativo{" "}
                <span className="text-white font-bold">"{successData.creative_name}"</span>
              </p>
            )}

            <div className="bg-[#1B1E28] border border-[#282D3D] rounded-xl p-3 mb-4 space-y-2 text-xs">
              <div className="flex items-center justify-between text-gray-300">
                <span>Saúde da Operação:</span>
                <span className="font-bold text-emerald-400">Impulsionada 📈</span>
              </div>
              <div className="flex items-center justify-between text-gray-300">
                <span>Streak de Vendas:</span>
                <span className="font-bold text-orange-400 flex items-center gap-1">
                  <Flame className="w-3.5 h-3.5 fill-orange-400" />
                  {successData.sales_streak} dias consecutivos!
                </span>
              </div>
              <div className="flex items-center justify-between text-gray-300">
                <span>XP Conquistado:</span>
                <span className="font-bold text-[#FF6B00]">+150 XP</span>
              </div>
            </div>

            <button
              data-testid="sale-done-btn"
              onClick={handleClose}
              className="w-full py-2.5 rounded-lg bg-[#FF6B00] hover:bg-[#FF8533] text-black font-extrabold text-xs uppercase tracking-wider"
            >
              Continuar Operação
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
